/**
 * Mock Data for Demo Mode
 *
 * Provides sample data when Firebase is not configured.
 */

export const mockUser = {
  uid: 'demo-user-001',
  email: 'owner@democompany.com',
  displayName: 'Demo Owner',
};

export const mockUserProfile = {
  name: 'Demo Owner',
  role: 'owner',
  phone: '(555) 123-4567',
  orgId: 'demo-org-001',
};

export const mockEmployees = [
  {
    id: 'emp-001',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@democompany.com',
    phone: '(555) 234-5678',
    status: 'active',
    department: 'Engineering',
    position: 'Senior Developer',
    salary: 85000,
    startDate: '2023-03-15',
    ssn: '***-**-1234',
  },
  {
    id: 'emp-002',
    name: 'Michael Chen',
    email: 'michael.chen@democompany.com',
    phone: '(555) 345-6789',
    status: 'active',
    department: 'Marketing',
    position: 'Marketing Manager',
    salary: 72000,
    startDate: '2022-08-01',
    ssn: '***-**-5678',
  },
  {
    id: 'emp-003',
    name: 'Emily Rodriguez',
    email: 'emily.rodriguez@democompany.com',
    phone: '(555) 456-7890',
    status: 'on_leave',
    department: 'HR',
    position: 'HR Specialist',
    salary: 58000,
    startDate: '2023-01-10',
    ssn: '***-**-9012',
  },
  {
    id: 'emp-004',
    name: 'James Wilson',
    email: 'james.wilson@democompany.com',
    phone: '(555) 567-8901',
    status: 'active',
    department: 'Sales',
    position: 'Sales Representative',
    salary: 55000,
    startDate: '2024-02-20',
    ssn: '***-**-3456',
  },
  {
    id: 'emp-005',
    name: 'Lisa Thompson',
    email: 'lisa.thompson@democompany.com',
    phone: '(555) 678-9012',
    status: 'active',
    department: 'Engineering',
    position: 'QA Engineer',
    salary: 68000,
    startDate: '2023-06-01',
    ssn: '***-**-7890',
  },
];

export const mockLeaveRequests = [
  {
    id: 'leave-001',
    employeeId: 'emp-003',
    employeeName: 'Emily Rodriguez',
    leaveType: 'MEDICAL',
    status: 'approved',
    startDate: '2026-01-06',
    endDate: '2026-02-14',
    durationDays: 40,
    reason: 'Post-surgery recovery',
    createdAt: '2025-12-15T10:30:00Z',
  },
  {
    id: 'leave-002',
    employeeId: 'emp-001',
    employeeName: 'Sarah Johnson',
    leaveType: 'BONDING',
    status: 'pending',
    startDate: '2026-02-01',
    endDate: '2026-04-26',
    durationDays: 84,
    reason: 'Maternity leave - new baby expected',
    createdAt: '2026-01-02T14:15:00Z',
  },
  {
    id: 'leave-003',
    employeeId: 'emp-002',
    employeeName: 'Michael Chen',
    leaveType: 'FAMILY',
    status: 'pending',
    startDate: '2026-01-20',
    endDate: '2026-02-03',
    durationDays: 14,
    reason: 'Care for elderly parent after hospital discharge',
    createdAt: '2026-01-03T09:00:00Z',
  },
];

export const mockDashboardData = {
  metrics: {
    totalEmployees: 5,
    activeEmployees: 4,
    onLeave: 1,
    pendingRequests: 2,
    monthlyPremium: 1859.33,
    ytdPremiums: 1859.33,
  },
  compliance: {
    status: 'compliant',
    nextDeadline: '2026-04-30',
    nextDeadlineType: 'Q1 Report Due',
  },
  recentActivity: [
    { type: 'leave_request', message: 'Sarah Johnson submitted bonding leave request', timestamp: '2026-01-02T14:15:00Z' },
    { type: 'leave_request', message: 'Michael Chen submitted family leave request', timestamp: '2026-01-03T09:00:00Z' },
    { type: 'employee', message: 'Emily Rodriguez started medical leave', timestamp: '2026-01-06T08:00:00Z' },
  ],
  premiumTrend: [
    { month: 'Aug', amount: 1750.00 },
    { month: 'Sep', amount: 1780.50 },
    { month: 'Oct', amount: 1780.50 },
    { month: 'Nov', amount: 1820.00 },
    { month: 'Dec', amount: 1859.33 },
    { month: 'Jan', amount: 1859.33 },
  ],
  leaveDistribution: {
    family: 1,
    medical: 1,
    bonding: 1,
  },
};

export const mockEligibility = {
  isEligible: true,
  basePeriodWages: 42500,
  familyWeeksUsed: 0,
  medicalWeeksUsed: 0,
  totalWeeksUsed: 0,
};

export const mockConfig = {
  companyName: 'Demo Company Inc.',
  ein: '12-3456789',
  address: '123 Main Street',
  city: 'Minneapolis',
  state: 'MN',
  zipCode: '55401',
  phone: '(555) 123-4567',
  email: 'hr@democompany.com',
  employeeCount: 5,
  notifications: {
    emailLeaveRequests: true,
    emailApprovals: true,
    emailReminders: true,
    emailReports: false,
  },
};

export const mockPayrollHistory = [
  { id: 'pay-001', period: 'December 2025', totalWages: 338000 / 12, premium: 1859.33, employerShare: 929.67, employeeShare: 929.67, status: 'paid', paidDate: '2025-12-31' },
  { id: 'pay-002', period: 'November 2025', totalWages: 332000 / 12, premium: 1820.00, employerShare: 910.00, employeeShare: 910.00, status: 'paid', paidDate: '2025-11-30' },
  { id: 'pay-003', period: 'October 2025', totalWages: 325000 / 12, premium: 1780.50, employerShare: 890.25, employeeShare: 890.25, status: 'paid', paidDate: '2025-10-31' },
];

export const mockReports = [
  { id: 'rpt-001', type: 'Q4 2025', quarter: 'Q4', year: 2025, status: 'submitted', generatedAt: '2025-12-28T10:00:00Z', downloadUrl: '#' },
  { id: 'rpt-002', type: 'Q3 2025', quarter: 'Q3', year: 2025, status: 'submitted', generatedAt: '2025-09-28T10:00:00Z', downloadUrl: '#' },
];

// Simulate async delay
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Mock API responses
export const mockApi = {
  getDashboard: async () => {
    await delay(500);
    return mockDashboardData;
  },

  getEmployees: async () => {
    await delay(300);
    return { employees: mockEmployees };
  },

  getEmployee: async (id) => {
    await delay(200);
    return mockEmployees.find(e => e.id === id);
  },

  createEmployee: async (data) => {
    await delay(500);
    return { id: `emp-${Date.now()}`, ...data };
  },

  updateEmployee: async (id, data) => {
    await delay(500);
    return { id, ...data };
  },

  deleteEmployee: async (id) => {
    await delay(300);
    return { success: true };
  },

  getLeaveRequests: async () => {
    await delay(300);
    return { requests: mockLeaveRequests };
  },

  getLeaveRequest: async (id) => {
    await delay(200);
    return mockLeaveRequests.find(r => r.id === id);
  },

  submitLeaveRequest: async (data) => {
    await delay(500);
    return { id: `leave-${Date.now()}`, status: 'pending', ...data };
  },

  approveLeaveRequest: async (id) => {
    await delay(500);
    return { id, status: 'approved' };
  },

  denyLeaveRequest: async (id, reason) => {
    await delay(500);
    return { id, status: 'denied', denialReason: reason };
  },

  cancelLeaveRequest: async (id) => {
    await delay(300);
    return { id, status: 'cancelled' };
  },

  checkEligibility: async () => {
    await delay(300);
    return mockEligibility;
  },

  calculatePremiums: async () => {
    await delay(500);
    return {
      totalWages: 338000,
      rate: 0.66,
      monthlyPremium: 1859.33,
      employerShare: 929.67,
      employeeShare: 929.67,
    };
  },

  getPayrolls: async () => {
    await delay(300);
    return mockPayrollHistory;
  },

  processPayroll: async () => {
    await delay(800);
    return { success: true, premiumAmount: 1859.33 };
  },

  getReports: async () => {
    await delay(300);
    return mockReports;
  },

  generateDEEDReport: async (quarter, year) => {
    await delay(1000);
    return {
      id: `rpt-${Date.now()}`,
      quarter,
      year,
      status: 'generated',
      downloadUrl: '#',
      employees: mockEmployees.map(e => ({
        name: e.name,
        ssn: e.ssn,
        wages: e.salary / 4,
        premium: (e.salary / 4) * 0.0066,
      })),
    };
  },

  createChatSession: async () => {
    await delay(200);
    return { sessionId: `chat-${Date.now()}` };
  },

  sendChatMessage: async (sessionId, message) => {
    await delay(1000);
    const responses = {
      default: "I'm the PFML Assistant. I can help you with leave policies, premium calculations, eligibility questions, and DEED reporting. What would you like to know?",
      leave: "Minnesota PFML provides up to 12 weeks of family leave and 12 weeks of medical leave per year, with a combined maximum of 20 weeks. Employees need at least $3,500 in annual wages to be eligible.",
      premium: "Your premium rate is 0.66% (small employer rate for companies with 30 or fewer employees). This is split 50/50 between employer and employee. Your monthly premium is approximately $1,859.",
      eligibility: "To be eligible for PFML benefits, employees must have earned at least $3,500 in annual wages and work for a covered employer in Minnesota.",
    };

    const lowerMsg = message.toLowerCase();
    let response = responses.default;
    if (lowerMsg.includes('leave') || lowerMsg.includes('week')) response = responses.leave;
    if (lowerMsg.includes('premium') || lowerMsg.includes('rate') || lowerMsg.includes('cost')) response = responses.premium;
    if (lowerMsg.includes('eligib')) response = responses.eligibility;

    return { response };
  },

  config: {
    get: async () => {
      await delay(300);
      return mockConfig;
    },
    update: async (id, data) => {
      await delay(500);
      return { ...mockConfig, ...data };
    },
  },

  leave: {
    getBalance: async () => {
      await delay(300);
      return mockEligibility;
    },
  },

  employee: {
    list: async () => {
      await delay(300);
      return mockEmployees;
    },
  },
};

export default mockApi;
